import { useState } from "react";
import { Alert, Button, Checkbox, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import type { CardRefundCommand } from "@/api/cardRefunds";
import type { CardRefundDoc, CardRefundProofSubmissionDoc } from "@/firestore/cardRefunds";
import type { PaymentDoc } from "@/firestore/types";
import { Money } from "@/components/Money";
import { Mono } from "@/components/Mono";
import { env } from "@/config/env";
import { refundActionError, validLogIndex, validTransferHash } from "./presentation";

export type RefundDialogAction = CardRefundCommand["action"] | "retry-verification";
const LABELS: Record<RefundDialogAction, string> = {
  claim: "Start processing",
  "release-unsent": "Release — no transfer sent",
  "submit-transfer": "Verify transfer and mark done",
  "correct-transfer-proof": "Correct transfer evidence",
  "link-original-payment": "Link original payment",
  "retry-verification": "Retry verification",
};

export interface CardRefundDialogProps {
  refund: CardRefundDoc;
  action: RefundDialogAction;
  expectedUpdatedAt: string;
  disabled: boolean;
  pending: boolean;
  activeProof: CardRefundProofSubmissionDoc | null;
  originalPayment: PaymentDoc | null;
  paymentLoading: boolean;
  paymentError: Error | null;
  onPaymentLookup(id: string): void;
  onCommand(command: CardRefundCommand, updatedAt: string): Promise<unknown>;
  onClose(): void;
}

export function CardRefundDialog(props: CardRefundDialogProps) {
  const { refund, action } = props;
  const correcting = action === "correct-transfer-proof";
  const retrying = action === "retry-verification";
  const transfer = correcting || retrying || action === "submit-transfer";
  const linking = action === "link-original-payment";
  const [hash, setHash] = useState(
    correcting || retrying ? (refund.submittedProof?.txHash ?? "") : "",
  );
  const [logIndex, setLogIndex] = useState(
    correcting || retrying ? String(refund.submittedProof?.logIndex ?? "") : "",
  );
  const [paymentId, setPaymentId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stale = refund.updatedAt !== props.expectedUpdatedAt;
  const busy = props.pending || submitting;
  const payment = props.originalPayment;
  const matchedPayment =
    !!payment &&
    payment.id === paymentId.trim() &&
    payment.owner === refund.owner &&
    payment.amountMicroUsdc > 0 &&
    payment.state === "captured" &&
    payment.id !== refund.legacyPayment;
  const instructionVersion = refund.payoutInstructions?.version;
  const releasing = action === "release-unsent";
  const authorizationAttemptId = refund.authorizationRequest?.id;
  const valid =
    confirmed &&
    !stale &&
    !props.disabled &&
    !busy &&
    (!releasing ||
      (!!authorizationAttemptId &&
        !refund.submittedProof &&
        (refund.fulfillmentState === "authorizing" ||
          (refund.fulfillmentState === "assigned" && !!instructionVersion)))) &&
    (!transfer || (!!instructionVersion && validTransferHash(hash) && validLogIndex(logIndex))) &&
    (!correcting || props.activeProof?.state === "rejected") &&
    (!retrying ||
      (props.activeProof?.id === refund.submittedProof?.id &&
        props.activeProof?.state === "pending" &&
        props.activeProof.instructionVersion === instructionVersion &&
        refund.submittedProof?.txHash === hash &&
        String(refund.submittedProof.logIndex ?? "") === logIndex)) &&
    (!linking || (matchedPayment && !props.paymentLoading && !props.paymentError));

  async function submit() {
    if (!valid) return;
    let command: CardRefundCommand;
    if (action === "claim") command = { action, input: {} };
    else if (action === "release-unsent") {
      const releasedInstructionVersion =
        refund.fulfillmentState === "authorizing" ? null : instructionVersion;
      if (!authorizationAttemptId || releasedInstructionVersion === undefined) return;
      command = {
        action,
        input: {
          authorizationAttemptId,
          instructionVersion: releasedInstructionVersion,
          noTransferSent: true,
        },
      };
    } else if (action === "link-original-payment") {
      command = {
        action,
        input: { originalPayment: paymentId.trim(), confirmedSameOwnerPurchase: true },
      };
    } else {
      if (!instructionVersion) return;
      const input = {
        instructionVersion,
        txHash: hash.trim(),
        logIndex: logIndex.trim() === "" ? null : Number(logIndex),
        reimbursementAttested: true as const,
      };
      if (action === "correct-transfer-proof") {
        if (!props.activeProof) return;
        command = { action, input: { ...input, supersedes: props.activeProof.id } };
      } else command = { action: "submit-transfer", input };
    }
    setSubmitting(true);
    setError(null);
    try {
      await props.onCommand(command, props.expectedUpdatedAt);
      props.onClose();
    } catch (e) {
      setError(refundActionError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      opened
      title={LABELS[action]}
      onClose={() => {
        if (!busy) props.onClose();
      }}
      closeOnEscape={!busy}
      closeOnClickOutside={!busy}
      withCloseButton={!busy}
      size="lg"
      centered
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Stack>
          {env.isProd ? (
            <Alert color="orange">You are recording this action in production.</Alert>
          ) : null}
          <Group>
            <Text size="sm">Refund</Text>
            <Mono value={refund.id} short />
            <Money micro={refund.amountMicroUsdc} />
          </Group>
          {action === "claim" ? (
            <Text size="sm">
              Claim this case before transferring. Your assignment is recorded first; the backend
              then checks refund eligibility and prepares current Main instructions. Wait for those
              instructions before sending. Your assignment will not expire automatically.
            </Text>
          ) : null}
          {action === "release-unsent" ? (
            <Alert color="orange">
              Release only if you have not sent a transfer. If a transaction was broadcast or its
              outcome is uncertain, retain this claim and record the hash. Release confirmation may
              take time; the case cannot be claimed again while confirmation is pending.
            </Alert>
          ) : null}
          {transfer ? (
            <>
              <Text size="sm">
                {retrying
                  ? "Check the recorded transfer again. This keeps the same evidence submission and does not send money or create another payout."
                  : "Enter the transaction already sent to the snapshotted Main address. Verification checks the actual USDC transfer and its indexed receiving entry."}
              </Text>
              {correcting ? (
                <Alert color="orange">
                  This corrects evidence only. Do not send another transfer. Earlier submissions
                  remain in the case history.
                </Alert>
              ) : null}
              <TextInput
                label="Transaction hash"
                value={hash}
                onChange={(e) => {
                  setHash(e.currentTarget.value);
                  setConfirmed(false);
                }}
                disabled={busy}
                readOnly={retrying}
                required
                spellCheck={false}
                maxLength={66}
              />
              <TextInput
                label="Transfer log index (optional)"
                description="Leave blank when the transaction has one matching USDC transfer. Use the receipt log index if several match."
                value={logIndex}
                onChange={(e) => {
                  setLogIndex(e.currentTarget.value);
                  setConfirmed(false);
                }}
                disabled={busy}
                readOnly={retrying}
                inputMode="numeric"
              />
            </>
          ) : null}
          {linking ? (
            <>
              <Text size="sm">
                Link only a confirmed original purchase for this user. A matching merchant or amount
                is not proof of a relationship.
              </Text>
              <TextInput
                label="Original payment ID"
                value={paymentId}
                onChange={(e) => {
                  setPaymentId(e.currentTarget.value);
                  setConfirmed(false);
                  props.onPaymentLookup(e.currentTarget.value.trim());
                }}
                disabled={busy}
                required
              />
              {props.paymentLoading ? (
                <Text size="sm">Looking up payment…</Text>
              ) : props.paymentError ? (
                <Text size="sm" c="red">
                  The payment could not be loaded. Try again before linking.
                </Text>
              ) : paymentId.trim() ? (
                matchedPayment ? (
                  <Group>
                    <Text size="sm">{payment?.merchantName || "Card payment"}</Text>
                    <Money micro={payment?.amountMicroUsdc} />
                  </Group>
                ) : (
                  <Text size="sm" c="red">
                    Choose an existing positive, captured purchase belonging to this user.
                  </Text>
                )
              ) : null}
            </>
          ) : null}
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.currentTarget.checked)}
            disabled={busy}
            label={
              action === "claim"
                ? "I will handle this refund and review the returned payout instructions."
                : action === "release-unsent"
                  ? "I confirm that no transfer was sent for this refund."
                  : linking
                    ? "I confirmed this same-user purchase is the original payment."
                    : "I confirm this transfer reimburses this refund and has not been used for another funding purpose."
            }
          />
          {stale ? (
            <Alert color="orange">
              This refund changed. Close the dialog, review the latest case, and reopen the action.
              Keep any sent transaction hash.
            </Alert>
          ) : null}
          {error ? (
            <Alert color="red">
              <Text size="sm">{error}</Text>
              {transfer ? (
                <Text size="sm" mt="xs">
                  Keep this hash. Retry recording the same evidence; do not send again.
                </Text>
              ) : null}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" disabled={busy} onClick={props.onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid} loading={busy}>
              {LABELS[action]}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
